import React from 'react';

import { FormatoDoNumero } from '../../api/radarApi';
import { formatarNumero } from './formatarNumero';
import '../como-funciona/help-circle-btn.css';

interface Props {
  titulo: string;
  /** O número em destaque. Vem SEMPRE do servidor — de um detector ou da consulta que ancora o achado. */
  numero: number;
  formato: FormatoDoNumero;
  /** "placas", "associados", "dos associados". Ausente quando o número já se explica. */
  unidade?: string;
  /** Valor secundário em dinheiro, ao lado da contagem ("200 placas · -R$ 227.528,92"). */
  valor?: number | null;
  texto: string;
  /** "Candidato" ou "Aprovado pela equipe" — é o selo que carrega a separação exigida pelo ADR 0006. */
  selo?: { rotulo: string; classe: string };
  /** Abre o `?` de proveniência. É a única via de auditar o número. */
  onExplicar: () => void;
  acoes?: React.ReactNode;
  rodape?: React.ReactNode;
}

/**
 * O cartão de um radar (transversal/at-82) — a mesma forma para achado pré-definido e do assistente.
 *
 * ⚠️ **A separação entre os dois é feita pelo SELO e pelo texto de apoio da seção, nunca pela
 * forma** (decisão do Victor, 02/09/2026: "tudo tem que ser padronizado"). Não reintroduzir borda
 * tracejada, cor de fundo própria nem moldura para o candidato — ver a nota de 02/09/2026 no
 * ADR 0006 antes de mexer nisso.
 *
 * ⚠️ O selo fica no TOPO, ao lado do `?`. Como linha própria — que era o que o painel do
 * Relacionamento fazia (`DIV-1` da análise) — ele empurra o número para baixo e desalinha as duas
 * seções da mesma tela.
 *
 * Dinheiro é sempre VERMELHO e contagem é azul, nos dois tipos de cartão: antes o mesmo valor
 * aparecia em cores diferentes conforme a seção.
 */
const RadarCard: React.FC<Props> = ({
  titulo,
  numero,
  formato,
  unidade,
  valor,
  texto,
  selo,
  onExplicar,
  acoes,
  rodape,
}) => (
  <article className="do-card radar-card">
    <div className="radar-card-topo">
      <span className="do-card-titulo">{titulo}</span>
      <div className="radar-card-topo-acoes">
        {selo && <span className={selo.classe}>{selo.rotulo}</span>}
        <button
          type="button"
          className="help-circle-btn"
          title="De onde vem este dado"
          onClick={onExplicar}
        >
          ?
        </button>
      </div>
    </div>

    <div className="radar-numero">
      <span className={formato === 'moeda' ? 'radar-valor-destaque' : 'radar-numero-valor'}>
        {formatarNumero(numero, formato)}
      </span>
      {unidade && <span className="radar-numero-unidade">{unidade}</span>}
      {valor !== null && valor !== undefined && (
        <span className="radar-valor">{formatarNumero(valor, 'moeda')}</span>
      )}
    </div>

    <p className="radar-recomendacao">{texto}</p>

    {acoes && <div className="radar-card-acoes">{acoes}</div>}

    {rodape}
  </article>
);

export default RadarCard;
